/**
 * Public page action header for live Link-in-Bio pages.
 *
 * Sits at the very top of every public layout (Classic / Instagram Grid /
 * Mixed) and exposes:
 *   • Tadlo logo (top-left)          → opens tadlo.ai in new tab
 *   • Bell icon → SubscribeModal     → only shown when email capture is enabled
 *   • Share icon → ShareModal        → always shown; native share used when available
 *
 * Styling is theme-aware — adapts to light, dark, and custom-colour creator
 * backgrounds via the `hasDarkBg` prop. The whole bar is a slim elevated
 * pill so icons stay readable over gradients, images, and solid colours.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Bell, Share2, X, Copy, Check, Mail, Instagram, User,
  Facebook, Linkedin, MessageCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// ───────────────────────────────────────────────────────────
//  PublicPageActionHeader — the slim branded top bar
// ───────────────────────────────────────────────────────────
export default function PublicPageActionHeader({ page, hasDarkBg, isPreview, showChrome }) {
  const [modal, setModal] = useState(null); // "subscribe" | "share" | null

  // Hide in preview mode — the BioEditorPage has its own chrome
  // Aug 2026 — `showChrome` decides this now, not `isPreview`. Theme
  // thumbnails are "preview" in every other sense (compact, inert, no
  // fetches) but MUST show the chrome: the logo and share button are
  // always-present furniture, so a preset has to be judged with them in
  // frame. `isPreview` stays as the fallback so existing callers behave
  // exactly as before.
  const visible = showChrome ?? !isPreview;
  if (!visible) return null;

  // Backend exposes `has_email_capture` as the single source of truth. Fall
  // back to nested config only for older cached payloads; the flag replaces
  // every historical "bell sometimes missing" edge case.
  const showBell =
    typeof page?.has_email_capture === "boolean"
      ? page.has_email_capture
      : !!page?.email_capture?.enabled;

  // Theme-adaptive pill surface — subtle on light, glass on dark
  const pillClass = hasDarkBg
    ? "bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/10"
    : "bg-white/90 hover:bg-white backdrop-blur-md text-gray-800 border border-black/5 shadow-sm";
  const iconBtnClass = `w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-[1.05] active:scale-[0.95] ${pillClass}`;
  const groupClass = `flex items-center gap-1.5 p-1 rounded-full ${pillClass}`;

  return (
    <>
      <div
        className="sticky top-0 z-40 w-full"
        data-testid="public-page-action-header"
      >
        <div className="max-w-lg mx-auto px-3 pt-3 flex items-center justify-between">
          {/* Top-left: Tadlo logo */}
          <a
            href="https://tadlo.ai"
            target="_blank"
            rel="noopener noreferrer"
            className={`${iconBtnClass} overflow-hidden`}
            data-testid="public-header-tadlo-logo"
            aria-label="Tadlo"
            title="Tadlo — make your own link in bio"
          >
            <img
              src="/tadlo-logo.jpeg"
              alt="Tadlo"
              className="w-full h-full object-cover"
              loading="lazy"
            
              // The brandmark is an asset request like any other. If it
              // 404s or is blocked, fall back to a wordmark instead of
              // leaving the browser's broken-image glyph in the chrome of
              // every themed page.
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = "none";
                const parent = el.parentElement;
                if (parent && !parent.querySelector("[data-testid='tadlo-mark-fallback']")) {
                  const mark = document.createElement("span");
                  mark.setAttribute("data-testid", "tadlo-mark-fallback");
                  mark.textContent = "T";
                  mark.style.cssText =
                    "display:flex;align-items:center;justify-content:center;" +
                    "width:100%;height:100%;font:700 11px/1 Inter,sans-serif;" +
                    "background:linear-gradient(135deg,#FFAE00,#FF0F7B,#682AE9);color:#fff";
                  parent.appendChild(mark);
                }
              }}
            />
          </a>

          {/* Top-right: grouped actions pill */}
          <div className={groupClass} data-testid="public-header-actions">
            {showBell && (
              <button
                type="button"
                onClick={() => setModal("subscribe")}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                data-testid="public-header-bell-btn"
                aria-label={`Subscribe to ${page?.display_name || page?.username || "this page"}`}
              >
                <Bell className="w-[17px] h-[17px]" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setModal("share")}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              data-testid="public-header-share-btn"
              aria-label="Share this page"
            >
              <Share2 className="w-[17px] h-[17px]" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {modal === "subscribe" && showBell && (
          <SubscribeModal page={page} onClose={() => setModal(null)} />
        )}
        {modal === "share" && (
          <ShareModal page={page} onClose={() => setModal(null)} />
        )}
      </AnimatePresence>
    </>
  );
}

// ───────────────────────────────────────────────────────────
//  SubscribeModal — lightweight email capture
// ───────────────────────────────────────────────────────────
function SubscribeModal({ page, onClose }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(""); // "" | "sending" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useScrollLock();
  useEscClose(onClose);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !page?.id) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Please enter a valid email address");
      return;
    }
    setErrorMsg("");
    setStatus("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/bio/lead`, { page_id: page.id, email });
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) setErrorMsg("Too many requests. Please wait a moment.");
      else if (err.response?.status === 400) setErrorMsg(detail || "Invalid email address");
      else setErrorMsg("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  const headline = page?.username
    ? `Subscribe to @${page.username}`
    : `Stay updated with ${page?.display_name || "this creator"}`;

  return (
    <ModalShell onClose={onClose} testid="subscribe-modal">
      <div className="flex flex-col items-center text-center">
        {page?.profile_image ? (
          <img
            src={page.profile_image}
            alt=""
            className="w-16 h-16 rounded-full object-cover mb-3 border-2 border-white shadow-md"
          />
        ) : (
          <div className="w-16 h-16 rounded-full mb-3 bg-gradient-to-tr from-[#FFAE00] via-[#FF0F7B] to-[#682AE9] flex items-center justify-center">
            <User className="w-7 h-7 text-white" />
          </div>
        )}

        {status === "success" ? (
          <>
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
              <Check className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-lg font-semibold mb-1">You're on the list!</h2>
            <p className="text-sm text-gray-500 mb-5">
              {page?.display_name || "This creator"} will email you updates directly.
            </p>
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
              data-testid="subscribe-modal-done"
            >
              Done
            </button>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold mb-1" data-testid="subscribe-modal-headline">
              {headline}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Drop your email to get updates, new links, and launches.
            </p>
            <form onSubmit={submit} className="w-full space-y-2">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrorMsg(""); }}
                  placeholder="you@example.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500"
                  data-testid="subscribe-modal-email-input"
                  autoFocus
                  required
                />
              </div>
              {errorMsg && (
                <p className="text-xs text-red-500 text-left" data-testid="subscribe-modal-error">
                  {errorMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#FF0F7B] to-[#682AE9] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-wait"
                data-testid="subscribe-modal-submit"
              >
                {status === "sending" ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
            <p className="text-[11px] text-gray-400 mt-3 leading-snug">
              We'll never spam. You can unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </ModalShell>
  );
}

// ───────────────────────────────────────────────────────────
//  ShareModal — social + copy link
// ───────────────────────────────────────────────────────────
function ShareModal({ page, onClose }) {
  const [copied, setCopied] = useState(false);

  useScrollLock();
  useEscClose(onClose);

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const name = page?.display_name || page?.username || "This page";
  const username = page?.username ? `@${page.username}` : "";
  const shareText = page?.username
    ? `Check out @${page.username} on Tadlo`
    : `Check out ${name}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers — not worth a toast, clipboard errors are rare
    }
  };

  const useNative = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: name, text: shareText, url: pageUrl });
    } catch { /* user cancelled */ }
  };

  // 5 circular share buttons — Copy sits first as the primary action, then
  // the 4 social channels. `onClick` variant for Copy keeps it a button;
  // the rest open in a new tab via `href`.
  const shareButtons = [
    {
      key: "copy",
      label: copied ? "Copied!" : "Copy link",
      icon: copied ? Check : Copy,
      colorClass: copied
        ? "bg-emerald-500 text-white"
        : "bg-gray-900 text-white hover:bg-gray-800",
      onClick: copy,
    },
    {
      key: "twitter",
      label: "X",
      icon: XLogo,
      colorClass: "bg-black text-white hover:opacity-90",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      colorClass: "bg-[#1877F2] text-white hover:opacity-90",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      colorClass: "bg-[#25D366] text-white hover:opacity-90",
      url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${pageUrl}`)}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      colorClass: "bg-[#0A66C2] text-white hover:opacity-90",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`,
    },
  ];

  return (
    <ModalShell onClose={onClose} testid="share-modal">
      {/* Preview card — profile + name + @username */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 mb-5">
        {page?.profile_image ? (
          <img src={page.profile_image} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#FFAE00] via-[#FF0F7B] to-[#682AE9] flex items-center justify-center shrink-0">
            <Instagram className="w-6 h-6 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-semibold truncate text-gray-900" data-testid="share-modal-name">
            {name}
          </p>
          {username && (
            <p className="text-xs text-gray-500 truncate" data-testid="share-modal-username">
              {username}
            </p>
          )}
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-3 text-left">Share via</h3>

      {/* 5 circular buttons in a single equally-spaced row */}
      <div className="grid grid-cols-5 gap-2 mb-5" data-testid="share-modal-socials">
        {shareButtons.map((b) => {
          const Inner = (
            <>
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${b.colorClass} transition-transform group-hover:scale-105 active:scale-95`}
              >
                <b.icon className="w-5 h-5" />
              </div>
              <span className="text-[11px] text-gray-600 text-center whitespace-nowrap">
                {b.label}
              </span>
            </>
          );
          return b.onClick ? (
            <button
              key={b.key}
              type="button"
              onClick={b.onClick}
              className="flex flex-col items-center gap-1.5 group"
              data-testid={`share-modal-${b.key}`}
            >
              {Inner}
            </button>
          ) : (
            <a
              key={b.key}
              href={b.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 group"
              data-testid={`share-modal-${b.key}`}
            >
              {Inner}
            </a>
          );
        })}
      </div>

      {typeof navigator !== "undefined" && navigator.share && (
        <button
          type="button"
          onClick={useNative}
          className="w-full py-2.5 rounded-xl border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          data-testid="share-modal-native"
        >
          More options…
        </button>
      )}
    </ModalShell>
  );
}

// ───────────────────────────────────────────────────────────
//  Shared modal shell — handles backdrop, mobile bottom-sheet,
//  focus trapping (via autofocus), and close-on-backdrop-click.
// ───────────────────────────────────────────────────────────
function ModalShell({ children, onClose, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={testid}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="w-full sm:w-[380px] max-w-[92vw] bg-white rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 shadow-2xl relative text-gray-900"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
          aria-label="Close"
          data-testid={`${testid}-close`}
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </motion.div>
    </motion.div>
  );
}

// ───────────────────────────────────────────────────────────
//  hooks
// ───────────────────────────────────────────────────────────
function useScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
}

function useEscClose(onClose) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
}

// Minimal X (Twitter) logo — lucide-react doesn't ship one
function XLogo({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
